import React, { useContext, lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import "./App.css";
import Navbar from "./Components/Navbar/Navbar";
import Footer from "./Components/Footer/Footer";
import { ApiContext } from "./Context/ApiProvider";

// Custom JWT Decoder
const decodeJWT = (token) => {
	try {
		const base64Url = token.split(".")[1];
		const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
		const jsonPayload = decodeURIComponent(
			atob(base64)
				.split("")
				.map((c) => `%${("00" + c.charCodeAt(0).toString(16)).slice(-2)}`)
				.join("")
		);
		return JSON.parse(jsonPayload);
	} catch (e) {
		return null;
	}
};

// Lazy load pages
const Home = lazy(() => import("./Pages/Home/Home"));
const Categories = lazy(() => import("./Pages/Categories/Categories"));
const Cart = lazy(() => import("./Pages/Cart/Cart"));
const Profile = lazy(() => import("./Components/Profile/Profile"));
const Orders = lazy(() => import("./Pages/Orders/Orders"));
const Register = lazy(() => import("./Pages/Register/Register"));
const Login = lazy(() => import("./Pages/Login/Login"));
const Forgot = lazy(() => import("./Pages/Forgot/Forgot"));
const ProductModal = lazy(() => import("./Pages/ProductModal/ProductModal"));
const NotFound = lazy(() => import("./Pages/404/NotFound"));
const PaymentCallback = lazy(() =>
	import("./Pages/PaymentCallback/PaymentCallback")
);
const Order = lazy(() => import("./Pages/Order/Order"));
const Offers = lazy(() => import("./Pages/Offer/Offer"));

const Loading = () => (
	<div className="Loading">
		<div className="loader">
			<div className="inner one"></div>
			<div className="inner two"></div>
			<div className="inner three"></div>
		</div>
	</div>
);

const App = () => {
	const {
		isUserDataLoaded,
		paymentData,
		shippingData,
		products,
		cartItems,
		handleLogout,
		handleLogin,
	} = useContext(ApiContext);

	const location = useLocation();
	const [isTokenValid, setIsTokenValid] = useState(false);

	useEffect(() => {
		const token = localStorage.getItem("token");
		if (token) {
			const decoded = decodeJWT(token);
			if (decoded && decoded.exp * 1000 > Date.now()) {
				setIsTokenValid(true);
			} else {
				setIsTokenValid(false);
			}
		} else {
			setIsTokenValid(false);
		}
	}, []);

	// Determine whether to render Navbar and Footer based on the current path
	const shouldRenderNavbar =
		window.innerWidth > 768
			? isUserDataLoaded &&
			  (location.pathname === "/home" || location.pathname !== "/") &&
			  !location.pathname.includes("/login")
			: isUserDataLoaded &&
			  (location.pathname === "/home" || location.pathname !== "/") &&
			  !location.pathname.includes("/login");

	const shouldRenderFooter =
		isUserDataLoaded &&
		!["/login", "/register", "/forgot", "/consult-chats"].includes(
			location.pathname
		);

	const renderProtectedRoutes = () => (
		<>
			<Route
				path="/cart"
				element={
					isTokenValid ? (
						<Cart cartItemsData={cartItems} products={products} />
					) : (
						<Navigate to="/login" replace />
					)
				}
			/>
			<Route
				path="/account"
				element={
					isTokenValid ? (
						<Profile
							handleLogout={handleLogout}
							shippingData={shippingData}
							paymentData={paymentData}
						/>
					) : (
						<Navigate to="/login" replace />
					)
				}
			/>

			<Route
				path="/profile"
				element={
					isTokenValid ? (
						<Profile
							handleLogout={handleLogout}
							shippingData={shippingData}
							paymentData={paymentData}
						/>
					) : (
						<Navigate to="/login" replace />
					)
				}
			/>
			<Route
				path="/orders"
				element={isTokenValid ? <Orders /> : <Navigate to="/login" replace />}
			/>
			<Route
				path="/order/:id"
				element={isTokenValid ? <Order /> : <Navigate to="/login" replace />}
			/>
		</>
	);

	const renderUnprotectedRoutes = () => (
		<>
			<Route
				path="/login"
				element={
					isTokenValid ? (
						<Navigate to="/" replace />
					) : (
						<Login onLogin={handleLogin} />
					)
				}
			/>
			<Route path="/register" element={<Register />} />
			<Route path="/forgot" element={<Forgot />} />
		</>
	);

	return (
		<div className="App">
			{shouldRenderNavbar && (
				<Navbar className={location.pathname === "/" ? "Transparent" : ""} />
			)}
			<Suspense fallback={<Loading />}>
				{isUserDataLoaded ? (
					<Routes>
						<Route path="/" element={<Home products={products} />} />
						<Route
							path="/product/:id"
							element={
								<ProductModal products={products} cartItems={cartItems} />
							}
						/>
						<Route
							path="/products"
							element={<Categories products={products} />}
						/>
						<Route path="/offers" element={<Offers products={products} />} />
						<Route path="/payment" element={<PaymentCallback />} />
						{renderProtectedRoutes()}
						{renderUnprotectedRoutes()}
						<Route path="*" element={<NotFound />} />
					</Routes>
				) : (
					<Loading />
				)}
			</Suspense>
			{shouldRenderFooter && <Footer />}
		</div>
	);
};

export default App;
